import { defineStore }             from "pinia"
import { computed, readonly, ref } from "vue"
import UserConfigService           from "@/api/UserConfigService"
import AuthService                 from "@/api/AuthService"
import ProjectService              from "@/api/taskItem/ProjectService"

export interface UserPermissions {
  canAccessResourcePlanning: boolean
}

export interface UserInfo {
  id: number | null
  cprId: number | null
  name: string | null
  permissions: UserPermissions
}

export const useUserStore = defineStore("user", () => {
  const userConfigService = new UserConfigService()
  const authService = new AuthService()
  const projectService = new ProjectService()

  const config = ref()
  const workProjectId = ref<number | null>(null)
  const workProject = ref<any>(null)
  const userInfo = ref<UserInfo>({
    id: null,
    cprId: null,
    name: null,
    permissions: {
      canAccessResourcePlanning: false
    }
  })
  const isLoadingUserInfo = ref(false)

  const getDefaultConfig = () => {
    return { selectedExtendedFilterId: null }
  }

  config.value = getDefaultConfig()

  const loadUserConfig = async (type: string, key: string) => {
    const response = await userConfigService.getValue(type, key)
    if (response.success && response.data) {
      // for extended-filter, extract selectedExtendedFilterId from items
      if (type === "extended-filter") {
        const exfDefaultItem = response.data.items.find(item => item.key === "exf-default-id")
        config.value = { selectedExtendedFilterId: exfDefaultItem?.value ?? null }
      } else {
        config.value = response.data
      }
    } else {
      config.value = getDefaultConfig()
    }
  }

  const loadWorkProject = async () => {
    const response = await userConfigService.getValue<number>("project", "workProjectId")
    if (response.success && response.data && response.data.value) {
      const projectId = response.data.value

      // load project data
      const projectResponse = await projectService.getById(projectId)
      if (projectResponse.success && projectResponse.data) {
        // only set both values if project was loaded successfully
        workProjectId.value = projectId
        workProject.value = projectResponse.data
      } else {
        // reset if project can't be loaded (might be deleted or inaccessible)
        workProjectId.value = null
        workProject.value = null
      }
    }
  }

  const loadUserInfo = async () => {
    if (isLoadingUserInfo.value) {
      return
    }

    isLoadingUserInfo.value = true
    try {
      const response = await authService.getUserInfo()
      if (response.success && response.data) {
        // Extract user info from companyPersonRelations if available
        const userRelation = response.data.companyPersonRelations?.[0]

        userInfo.value = {
          id: response.data.id || null,
          cprId: userRelation?.id || null,
          name: userRelation?.name || null,
          permissions: {
            canAccessResourcePlanning: response.data.permissions?.canAccessResourcePlanning || false
          }
        }
      } else {
        console.error("Failed to load user info:", response.error || response.message)
      }
    } catch (error) {
      console.error("Error loading user info:", error)
    } finally {
      isLoadingUserInfo.value = false
    }
  }

  const hasPermission = (permission: keyof UserPermissions): boolean => {
    return userInfo.value.permissions[permission] || false
  }

  const canAccessResourcePlanning = computed(() => hasPermission("canAccessResourcePlanning"))

  const resetUserData = () => {
    userInfo.value = {
      id: null,
      cprId: null,
      name: null,
      permissions: {
        canAccessResourcePlanning: false
      }
    }
  }

  return {
    config,
    workProjectId: readonly(workProjectId),
    workProject: readonly(workProject),
    userInfo: readonly(userInfo),
    isLoadingUserInfo: readonly(isLoadingUserInfo),
    canAccessResourcePlanning,
    loadUserConfig,
    loadWorkProject,
    loadUserInfo,
    hasPermission,
    resetUserData
  }
})
